import { BASE_URL, PLANIFICO_WEBSERVER_BASE_URL } from "@/global.ts";
import type { Constraint, Definition, EquityWeights, Schedule, SolverConfigs, WeekendGrid, WeightOverride } from "./types";
import type { EquityTargetData, SetExtendedWgDataRequest } from "./types/needsContrainsRequest";
import type { AnnualConfig, SummaryColumn, RoleGroupColumn, RoleGroupConfig } from "./equityInterfaces";


const headers = {
    'Content-Type': 'application/json',
    'Accept': 'application/json,text/plain',
};

const getDepId = (): string => {
    const params = new URLSearchParams(window.location.search);
    return `${params.get("bizId")}_${params.get("wgId")}`;
};

let mutationVersion = 0;

export const getMutationVersion = (): number => mutationVersion;

export const applyChanges = async (loadedSchedule): Promise<Definition[]> => {
    const params = new URLSearchParams(window.location.search);
    var payloadObj = {
        fromEpoch: params.get("fromEpoch"),
        toEpoch: params.get("toEpoch"),
        wgId: params.get("wgId"),
        lang: params.get("lang"),
        bizId: params.get("bizId"),
        result: loadedSchedule
    };

    const res = await fetch(PLANIFICO_WEBSERVER_BASE_URL + "api_V2/ApplyAdvanceScheduleShifts", {
        headers: {
            "Content-Type": "application/json",
            "connectionuserid": params.get("userId"),
            "connectionsessionid": params.get("sessionId")
        },
        method: "POST",
        body: JSON.stringify(payloadObj)
    })
    if (!res.ok) throw res;
    return res.json();
}
export const getScheduleData = async (): Promise<Schedule> => {
    const params = new URLSearchParams(window.location.search);
    var payloadObj = {
        fromEpoch: params.get("fromEpoch"),
        toEpoch: params.get("toEpoch"),
        wgId: params.get("wgId"),
        lang: params.get("lang"),
        bizId: params.get("bizId"),
        //result: loadedSchedule
    };

    const res = await fetch(PLANIFICO_WEBSERVER_BASE_URL + "api_V2/GetAdvanceScheduleDomainData ", {
        headers: {
            "Content-Type": "application/json",
            "connectionuserid": params.get("userId"),
            "connectionsessionid": params.get("sessionId")
        },
        method: "POST",
        body: JSON.stringify(payloadObj)
    })
    if (!res.ok) throw res;
    return res.json();
}

export const fetchDefinitions = async (): Promise<Definition[]> => {
    const res = await fetch(BASE_URL + "constraints/def", {
        method: "GET"
    })
    if (!res.ok) throw res;
    return res.json();
}

export const fetchWeightOverrides = async (): Promise<WeightOverride[]> => {
    const params = new URLSearchParams(window.location.search);
    const bizId = params.get("bizId")
    const wgId = params.get("wgId")
    const path = `${BASE_URL}constraints/weight?depId=${bizId + "_" + wgId}`
    const res = await fetch(path);
    if (!res.ok) throw res;
    return res.json();
};

export const fetchConstraints = async (): Promise<Constraint[]> => {
    const params = new URLSearchParams(window.location.search);
    const bizId = params.get("bizId")
    const wgId = params.get("wgId")
    const path = `${BASE_URL}constraints?depId=${bizId + "_" + wgId}`
    const res = await fetch(path);
    if (!res.ok) throw res;
    return res.json();
};

export const fetchSolverConfigs = async (): Promise<SolverConfigs> => {
    const params = new URLSearchParams(window.location.search);
    const bizId = params.get("bizId")
    const wgId = params.get("wgId")
    const path = `${BASE_URL}constraints/solverconfigs?bizId=${bizId}&wgId=${wgId}`
    const res = await fetch(path);
    if (!res.ok) throw res;
    return res.json();
};
export const updateSolverConfigs = async (configs: SolverConfigs): Promise<SolverConfigs> => {
    // const depId = getDepId();
    const params = new URLSearchParams(window.location.search);
    const bizId = params.get("bizId")
    const wgId = params.get("wgId")

    const path = `${BASE_URL}constraints/solverconfigs/save?bizId=${bizId}&wgId=${wgId}`
    const res = await fetch(path, {
        method: "POST",
        headers,
        body: JSON.stringify(configs)
    });

    if (!res.ok) {
        throw res;
    }

    mutationVersion++;

    return configs;
};

export const fetchSchedule = async (scheduleId?: string | null): Promise<Schedule> => {
    const depId = getDepId();
    const queryString = window.location.search;
    const params = new URLSearchParams(window.location.search);
    const bizId = params.get("bizId")
    const wgId = params.get("wgId")

    const path = scheduleId ? `${BASE_URL}schedules/${scheduleId}?depId=${depId}&bizId=${bizId}&wgId=${wgId}` : `${BASE_URL}demo-data/${queryString}`;
    const res = await fetch(path);
    if (!res.ok) throw res;
    return res.json();
};

export const fetchScheduleCount = async (): Promise<{ id: string, label: string }[]> => {
    const depId = getDepId();
    const path = `${BASE_URL}schedules/save?depId=${depId}`;

    const res = await fetch(path);
    if (!res.ok) throw res;
    return res.json();
};

export const fetchSavedSchedule = async (id: string): Promise<string> => {
    const params = new URLSearchParams(window.location.search);
    const depId = params.get("bizId") + "_" + params.get("wgId")
    const res = await fetch(`${BASE_URL}schedules/save/${id}?depId=${depId}`, { method: 'GET', headers });
    if (!res.ok) throw res;
    return res.json();
};

export const deleteSavedSchedule = async (id: string): Promise<void> => {
    const params = new URLSearchParams(window.location.search);
    const depId = params.get("bizId") + "_" + params.get("wgId")
    const res = await fetch(`${BASE_URL}schedules/save/${id}?depId=${depId}`, { method: 'DELETE', headers });
    if (!res.ok) throw res;
};

export const saveSchedule = async (schedule: Schedule, label?: string): Promise<{ id: string; label: string }> => {
    const params = new URLSearchParams(window.location.search);
    const depId = params.get("bizId") + "_" + params.get("wgId")
    const query = label ? `depId=${depId}&label=${encodeURIComponent(label)}` : `depId=${depId}`;
    const res = await fetch(`${BASE_URL}schedules/save?${query}`, {
        method: 'POST',
        headers,
        body: JSON.stringify(schedule)
    });
    if (!res.ok) throw res;
    return res.json();
};

export const benchmark = async (schedule: Schedule): Promise<string> => {
    const params = new URLSearchParams(window.location.search);
    const depId = params.get("bizId") + "_" + params.get("wgId")
    const res = await fetch(`${BASE_URL}schedules/benchmark`, {
        method: 'POST',
        headers,
        body: JSON.stringify(schedule)
    });
    if (!res.ok) throw res;
    return res.text();
};

export const startSolving = async (schedule: Schedule, phaseId?: string): Promise<string> => {
    const depId = getDepId();
    const params = new URLSearchParams(window.location.search);
    const bizId = params.get("bizId");
    const wgId = params.get("wgId");
    let url = `${BASE_URL}schedules?depId=${depId}&bizId=${bizId}&wgId=${wgId}`;
    if (phaseId) url += `&phaseId=${phaseId}`
    const payload = { ...schedule, roleCombinations: (await buildRoleCombinationsMap()).map };
    const res = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload)
    });
    if (!res.ok) throw res;
    return res.text();
};

export const stopSolving = async (scheduleId: string, phaseId?: string): Promise<void> => {
    const depId = getDepId();
    const params = new URLSearchParams(window.location.search);
    const bizId = params.get("bizId");
    const wgId = params.get("wgId");
    let url = `${BASE_URL}schedules/${scheduleId}?depId=${depId}&bizId=${bizId}&wgId=${wgId}`;
    if (phaseId) url += `&phaseId=${phaseId}`
    const res = await fetch(url, { method: 'DELETE' });

    if (!res.ok) throw res;
};

export const analyzeSchedule = async (
    schedule: Schedule,
    phaseId?: string
): Promise<{ analysis: any; combinationsDegraded: boolean }> => {
    const depId = getDepId();
    const params = new URLSearchParams(window.location.search);
    const bizId = params.get("bizId");
    const wgId = params.get("wgId");
    let url = `${BASE_URL}schedules/analyze?depId=${depId}&bizId=${bizId}&wgId=${wgId}`;
    if (phaseId) url += `&phaseId=${phaseId}`
    const { map: roleCombinations, degraded } = await buildRoleCombinationsMap();
    const payload = { ...schedule, roleCombinations };
    const res = await fetch(url, {
        method: 'PUT',
        headers,
        body: JSON.stringify(payload)
    });
    if (!res.ok) throw res;
    let json = await res.json();
    return { analysis: json, combinationsDegraded: degraded }
};


export const fetchExtendedWgData = async (options?: { returnRelatedStaffRoles?: boolean }): Promise<any> => {
    const params = new URLSearchParams(window.location.search);
    const payloadObj: Record<string, string | null> = {
        wgId: params.get("wgId"),
        businessAccountId: params.get("bizId"),
    };

    if (options?.returnRelatedStaffRoles) {
        payloadObj.returnRelatedStaffRoles = "true";
        payloadObj.myUserId = params.get("userId");
    }

    const res = await fetch(PLANIFICO_WEBSERVER_BASE_URL + "BusinessAccount/GetExtendedWgData", {
        headers: {
            "Content-Type": "application/json",
            "connectionuserid": params.get("userId") || "",
            "connectionsessionid": params.get("sessionId") || ""
        },
        method: "POST",
        body: JSON.stringify(payloadObj)
    });


    if (!res.ok) {
        throw res;
    }

    const responseBody = await res.json();

    if (responseBody && responseBody.data) {
        if (typeof responseBody.data === 'string') {
            return JSON.parse(responseBody.data);
        }

        return responseBody.data;
    }

    return responseBody;
};

const buildRoleCombinationsMap = async (): Promise<{ map: Record<string, string[]>; degraded: boolean }> => {
    try {
        const data = await fetchExtendedWgData({ returnRelatedStaffRoles: true });

        if (data?.dicOfStaffRoles == null) {
            return { map: {}, degraded: true };
        }

        const dicOfStaffRoles = data.dicOfStaffRoles;
        const map: Record<string, string[]> = {};

        for (const roleId of Object.keys(dicOfStaffRoles)) {
            const allowed = Object.keys(dicOfStaffRoles[roleId]?.simulShiftsConfig?.dicOfWhiteListRules ?? {});
            if (allowed.length > 0) {
                map[roleId] = allowed;
            }
        }

        return { map, degraded: false };
    } catch (err) {
        console.error("Combinaisons de tâches indisponibles; la contrainte restera inerte", err);
        return { map: {}, degraded: true };
    }
};

export const updateExtendedWgData = async (payload: SetExtendedWgDataRequest): Promise<any> => {
    const params = new URLSearchParams(window.location.search);

    const res = await fetch(PLANIFICO_WEBSERVER_BASE_URL + "BusinessAccount/SetExtendedWgData", {
        headers: {
            "Content-Type": "application/json",
            "connectionuserid": params.get("userId") || "",
            "connectionsessionid": params.get("sessionId") || ""
        },
        method: "POST",
        body: JSON.stringify(payload)
    });

    if (!res.ok) {
        throw res;
    }

    mutationVersion++;

    return res.json();
};

export const updateStaffRoleCombinations = async (roleId: string, whiteListRoleIds: string[]): Promise<any> => {
    const params = new URLSearchParams(window.location.search);

    const dicOfWhiteListRules: Record<string, Record<string, never>> = {};
    for (const id of whiteListRoleIds) {
        dicOfWhiteListRules[id] = {};
    }

    const payload = {
        businessAccountId: params.get("bizId"),
        roleId,
        active: "true", // ATTENTION: mettre false fait un delete
        simulShiftsConfig: {
            dicOfWhiteListRules,
        },
    };

    const res = await fetch(PLANIFICO_WEBSERVER_BASE_URL + "BusinessAccount/ChangeRoleGenDetails", {
        headers: {
            "Content-Type": "application/json",
            "connectionuserid": params.get("userId") || "",
            "connectionsessionid": params.get("sessionId") || ""
        },
        method: "POST",
        body: JSON.stringify(payload)
    });

    if (!res.ok) {
        throw res;
    }

    mutationVersion++;

    return res.json();
};

export const fetchEquityTargets = async (): Promise<EquityTargetData> => {
    const depId = getDepId();
    const path = `${BASE_URL}constraints/equitytable?depId=${depId}`;
    const res = await fetch(path, {
        method: "GET",
        headers
    });

    if (!res.ok) {
        throw res;
    }

    const data = await res.json();
    return data || {};
};

export const updateEquityTargets = async (targets: EquityTargetData): Promise<EquityTargetData> => {
    const depId = getDepId();

    const path = `${BASE_URL}constraints/equitytable?depId=${depId}`;
    const res = await fetch(path, {
        method: "POST",
        headers,
        body: JSON.stringify(targets)
    });

    if (!res.ok) {
        throw res;
    }

    mutationVersion++;

    return targets;
};

export const fetchEquityConfig = async (): Promise<{
    ratioActive: boolean,
    maxActive: boolean,
    summaryActive: boolean,
    roleGroupActive: boolean
}> => {
    const depId = getDepId();
    const path = `${BASE_URL}constraints/equitytable/config?depId=${depId}`;
    const res = await fetch(path, {
        method: "GET",
        headers
    });

    if (!res.ok) {
        throw res;
    }

    return res.json();
};

export const toggleEquityActive = async (field: 'ratio' | 'max' | 'summary' | 'rolegroup', active: boolean): Promise<void> => {
    const depId = getDepId();
    const path = `${BASE_URL}constraints/equitytable/toggle?depId=${depId}&field=${field}&active=${active}`;
    const res = await fetch(path, {
        method: "POST",
        headers
    });

    if (!res.ok) {
        throw res;
    }

    mutationVersion++;
};

export const fetchSummaryColumns = async (): Promise<SummaryColumn[]> => {
    const depId = getDepId();
    const path = `${BASE_URL}constraints/equitytable/summary?depId=${depId}`;
    const res = await fetch(path, {
        method: "GET",
        headers
    });

    if (!res.ok) {
        throw res;
    }

    const data = await res.json();
    return data?.columns || [];
};

export const updateSummaryColumns = async (columns: SummaryColumn[]): Promise<SummaryColumn[]> => {
    const depId = getDepId();
    const path = `${BASE_URL}constraints/equitytable/summary?depId=${depId}`;
    const res = await fetch(path, {
        method: "POST",
        headers,
        body: JSON.stringify({ columns })
    });

    if (!res.ok) {
        throw res;
    }

    mutationVersion++;

    return columns;
};

export const fetchAnnualConfig = async (): Promise<AnnualConfig> => {
    const depId = getDepId();
    const path = `${BASE_URL}constraints/equitytable/annual?depId=${depId}`;
    const res = await fetch(path, {
        method: "GET",
        headers
    });

    if (!res.ok) {
        throw res;
    }

    const data = await res.json();
    return {
        columns: data?.columns || [],
        seededRoleIds: data?.seededRoleIds || [],
    };
};

export const updateAnnualConfig = async (columns: SummaryColumn[], seededRoleIds: string[]): Promise<SummaryColumn[]> => {
    const depId = getDepId();
    const path = `${BASE_URL}constraints/equitytable/annual?depId=${depId}`;
    const res = await fetch(path, {
        method: "POST",
        headers,
        body: JSON.stringify({ columns, seededRoleIds })
    });

    if (!res.ok) {
        throw res;
    }

    mutationVersion++;

    return columns;
};

export const fetchRoleGroupConfig = async (): Promise<RoleGroupConfig> => {
    const depId = getDepId();
    const path = `${BASE_URL}constraints/equitytable/rolegroup?depId=${depId}`;
    const res = await fetch(path, {
        method: "GET",
        headers
    });

    if (!res.ok) {
        throw res;
    }

    const data = await res.json();
    return {
        columns: data?.columns || [],
        refDays: data?.refDays ?? 28,
        desiredTargets: data?.desiredTargets || {},
    };
};

export const updateRoleGroupConfig = async (
    columns: RoleGroupColumn[],
    refDays: number,
    desiredTargets: Record<string, number>
): Promise<void> => {
    const depId = getDepId();
    const path = `${BASE_URL}constraints/equitytable/rolegroup?depId=${depId}&refDays=${refDays}`;
    const res = await fetch(path, {
        method: "POST",
        headers,
        body: JSON.stringify({ columns, desiredTargets })
    });

    if (!res.ok) {
        throw res;
    }

    mutationVersion++;
};

export const fetchWeekendGrid = async (): Promise<WeekendGrid> => {
    const depId = getDepId();
    const path = `${BASE_URL}constraints/equitytable/weekend-grid?depId=${depId}`;
    const res = await fetch(path, {
        method: "GET",
        headers
    });

    if (!res.ok) {
        throw res;
    }

    const data = await res.json();
    return data?.grid || {};
};

export const fetchEquityWeights = async (): Promise<EquityWeights> => {
    const depId = getDepId();
    const path = `${BASE_URL}constraints/equitytable/equity-weights?depId=${depId}`;
    const res = await fetch(path, {
        method: "GET",
        headers
    });

    if (!res.ok) {
        throw res;
    }

    const data = await res.json();

    return data?.weights ?? {};
};

export const updateEquityWeights = async (weights: EquityWeights): Promise<void> => {
    const depId = getDepId();
    const path = `${BASE_URL}constraints/equitytable/equity-weights?depId=${depId}`;
    const res = await fetch(path, {
        method: "POST",
        headers,
        body: JSON.stringify({ weights })
    });

    if (!res.ok) {
        throw res;
    }

    mutationVersion++;
};

export const updateWeekendGrid = async (grid: WeekendGrid): Promise<void> => {
    const depId = getDepId();
    const path = `${BASE_URL}constraints/equitytable/weekend-grid?depId=${depId}`;
    const res = await fetch(path, {
        method: "POST",
        headers,
        body: JSON.stringify({ grid })
    });

    if (!res.ok) {
        throw res;
    }

    mutationVersion++;
};
